/**
 * BulkStoreAssignments Component
 * 
 * [PHASE3B] Extracted from retailer-management.tsx
 * 
 * Handles bulk assignment of operators to store/format combinations.
 * Includes pagination, volume management, and optimistic updates.
 * 
 * Query Keys Used (UNCHANGED from original):
 * - ["/api/stores", retailerId]
 * - ["/api/media-formats", retailerId]
 * - ["/api/users/operators", retailerId]
 * - ["/api/store-format-operator-assignments", retailerId, selectedStores]
 * - ["/api/store-media-requirements", retailerId]
 */

// TODO(RQv5): React Query v5 type mismatch.
// Known pre-existing issue from v5 migration.
// Not related to campaign creation, snapshots, or posting instructions.
// Tracked separately as frontend technical debt.

import { useState, useMemo, useCallback } from "react";
import * as React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Edit, Trash2, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { apiFetch } from "@/lib/api";
import { unwrapPaginatedResponse } from "@/lib/http";
import { DataTable } from "@/components/tables/data-table";
import {
  createStoreFormatOperatorColumns,
  StoreFormatOperatorRow,
} from "@/components/tables/store-format-operator-columns";
import type {
  Store as StoreType,
  MediaFormat,
  StoreMediaRequirement,
  User,
  StoreFormatOperatorAssignment,
} from "@shared/schema";
import { shouldSendOperatorAssignment } from "@/components/retailer/storeMediaFormatRows";

// Component Props
export interface BulkStoreAssignmentsProps {
  retailerId: number;
  retailerName: string;
}

interface AssignmentsQueryResponse {
  data: any[];
  pagination: {
    total?: number;
    limit?: number;
    offset?: number;
    currentPage?: number;
    totalCount?: number;
    totalPages?: number;
    hasMore?: boolean;
  } | null;
}

// [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_2] Memoized assignment row component
const AssignmentRow = React.memo(({
  storeId,
  assignment,
  store,
  format,
  operator,
  currentVolume,
  isEditing,
  isEditingVolume,
  editingAssignment,
  editingVolume,
  operators,
  updateMutationPending,
  deleteMutationPending,
  volumeMutationPending,
  onEditStart,
  onEditCancel,
  onEditSave,
  onVolumeEditStart,
  onVolumeEditCancel,
  onVolumeSave,
  onDelete,
  setEditingAssignment,
  setEditingVolume,
}: any) => {
  return (
    <TableRow key={`${storeId}-${assignment.mediaFormatId}`}>
      <TableCell>{store?.name}</TableCell>
      <TableCell>{format?.name || 'Unknown Format'}</TableCell>
      <TableCell>
        {isEditing ? (
          <div className="flex items-center space-x-2">
            <Select
              value={editingAssignment.currentOperatorId.toString()}
              onValueChange={(value) => {
                console.log('[ASSIGNMENT_ROW_DROPDOWN] Selected value:', value, 'Current operatorId:', editingAssignment.currentOperatorId);
                console.log('[ASSIGNMENT_ROW_DROPDOWN] Full editingAssignment before update:', JSON.stringify(editingAssignment));
                const newState = {
                  ...editingAssignment,
                  currentOperatorId: parseInt(value)
                };
                console.log('[ASSIGNMENT_ROW_DROPDOWN] New state:', JSON.stringify(newState));
                setEditingAssignment(newState);
              }}
              onOpenChange={(open) => {
                console.log('[ASSIGNMENT_ROW_DROPDOWN] Dropdown open state:', open);
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {operators.map((op: any) => {
                  console.log('[ASSIGNMENT_ROW_DROPDOWN] Rendering operator option:', op.id, op.name);
                  return (
                    <SelectItem key={op.id} value={op.id.toString()}>
                      {op.name}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={onEditSave}
              disabled={updateMutationPending}
            >
              {updateMutationPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onEditCancel}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <span>{operator?.name || 'Unknown Operator'}</span>
        )}
      </TableCell>
      <TableCell>
        {isEditingVolume ? (
          <div className="flex items-center space-x-2">
            <Input
              type="number"
              min="0"
              value={editingVolume.currentVolume}
              onChange={(e) => setEditingVolume({
                ...editingVolume,
                currentVolume: parseInt(e.target.value) || 0
              })}
              className="w-20"
            />
            <Button
              size="sm"
              onClick={onVolumeSave}
              disabled={volumeMutationPending}
            >
              {volumeMutationPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onVolumeEditCancel}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <span className={currentVolume === 0 ? "text-red-500" : ""}>
              {currentVolume}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onVolumeEditStart(storeId, assignment.mediaFormatId, currentVolume)}
              title="Edit volume"
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        )}
      </TableCell>
      <TableCell>
        {assignment.createdAt ? new Date(assignment.createdAt).toLocaleDateString() : 'N/A'}
      </TableCell>
      <TableCell>
        {!isEditing && (
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onEditStart}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDelete(storeId, assignment.mediaFormatId)}
              disabled={deleteMutationPending}
              className="text-red-600 hover:text-red-700"
            >
              {deleteMutationPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}, (prevProps, nextProps) => {
  // [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_2] Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.assignment.operatorId === nextProps.assignment.operatorId &&
    prevProps.currentVolume === nextProps.currentVolume &&
    prevProps.isEditing === nextProps.isEditing &&
    prevProps.isEditingVolume === nextProps.isEditingVolume &&
    prevProps.editingAssignment?.currentOperatorId === nextProps.editingAssignment?.currentOperatorId &&
    prevProps.editingVolume?.currentVolume === nextProps.editingVolume?.currentVolume &&
    prevProps.updateMutationPending === nextProps.updateMutationPending &&
    prevProps.deleteMutationPending === nextProps.deleteMutationPending &&
    prevProps.volumeMutationPending === nextProps.volumeMutationPending
  );
});

AssignmentRow.displayName = 'AssignmentRow';

/**
 * BulkStoreAssignments - Main component for managing store/format/operator assignments
 */
export function BulkStoreAssignments({ retailerId, retailerName }: BulkStoreAssignmentsProps) {
  // [BULK_ASSIGNMENTS_OPTIMIZATION] All optimizations enabled by default
  const [selectedStores, setSelectedStores] = useState<number[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<number[]>([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(null);
  const [storeOperatorOverrides, setStoreOperatorOverrides] = useState<Record<number, number | null>>({});
  const [formatVolumes, setFormatVolumes] = useState<{ [formatId: number]: number }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [editingAssignment, setEditingAssignment] = useState<{ storeId: number, formatId: number, currentOperatorId: number } | null>(null);
  const [editingVolume, setEditingVolume] = useState<{ storeId: number; formatId: number; currentVolume: number } | null>(null);

  const { toast } = useToast();

  // [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_1] Retailer-scoped store fetch
  // [PHASE2_PAGINATION] Using unwrapPaginatedResponse for backward compatibility
  const { data: stores = [] } = useQuery<StoreType[]>({
    queryKey: ["/api/stores", retailerId],
    queryFn: async () => {
      const url = `/api/stores?retailerId=${retailerId}&pageSize=200`;
      console.log(`[BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_1] Fetching stores for retailer ${retailerId}`);
      const res = await apiFetch(url);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const json = await res.json();
      return unwrapPaginatedResponse<StoreType>(json);
    },
    enabled: !!retailerId,
    staleTime: 5 * 60 * 1000, // 5 minutes - stores don't change frequently
  });

  // [PHASE2_PAGINATION] Using unwrapPaginatedResponse for backward compatibility
  const { data: mediaFormats = [] } = useQuery<MediaFormat[]>({
    queryKey: ["/api/media-formats", retailerId],
    queryFn: async () => {
      const res = await apiFetch(`/api/media-formats?retailerId=${retailerId}&pageSize=200`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const json = await res.json();
      return unwrapPaginatedResponse<MediaFormat>(json);
    },
    enabled: !!retailerId,
    staleTime: 5 * 60 * 1000, // 5 minutes - formats don't change frequently
  });

  // [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_1] Conditionally use retailer-scoped or legacy operators fetch
  // [PHASE2_PAGINATION] Using unwrapPaginatedResponse for backward compatibility
  const { data: operators = [] } = useQuery<User[]>({
    queryKey: ["/api/users/operators", retailerId],
    queryFn: async () => {
      const url = `/api/users/operators?retailerId=${retailerId}&pageSize=200`;
      console.log(`[BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_1] Fetching operators for retailer ${retailerId}`);
      const res = await apiFetch(url);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const json = await res.json();
      return unwrapPaginatedResponse<User>(json);
    },
    enabled: !!retailerId,
    staleTime: 5 * 60 * 1000, // 5 minutes - operators don't change frequently
  });

  // Force refresh operators
  const refreshOperators = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/users/operators", retailerId] });
    toast({ title: "Operators refreshed" });
  };

  // Fetch all assignments for the retailer (client-side sorting/filtering/pagination via DataTable)
  const assignmentsQuery = useQuery<AssignmentsQueryResponse>({
    queryKey: ["/api/store-format-operator-assignments", retailerId, selectedStores.join(',')],
    queryFn: async () => {
      let url: string;

      if (selectedStores.length > 0) {
        url = `/api/store-format-operator-assignments?storeIds=${selectedStores.join(',')}`;
      } else {
        url = `/api/store-format-operator-assignments?retailerId=${retailerId}&withPagination=true&limit=5000&offset=0`;
      }

      const res = await apiFetch(url);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const data = await res.json();

      if (selectedStores.length > 0 && Array.isArray(data)) {
        return { data, pagination: null };
      }

      return data;
    },
    enabled: !!retailerId,
    staleTime: 2 * 60 * 1000,
  });

  const existingAssignments = assignmentsQuery.data?.data || [];

  // Fetch store media requirements for volume data (retailer-scoped)
  const { data: storeMediaRequirements = [] } = useQuery<StoreMediaRequirement[]>({
    queryKey: ["/api/store-media-requirements", retailerId],
    queryFn: async () => {
      const res = await apiFetch(`/api/store-media-requirements?retailerId=${retailerId}`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
    enabled: !!retailerId,
  });

  const bulkAssignMutation = useMutation({
    mutationFn: async (assignments: { storeId: number; mediaFormatId: number; operatorId: number }[]) => {
      const response = await apiRequest("/api/store-format-operator-assignments/bulk", "POST", { assignments });
      const result = await response.json();
      console.log("📦 Bulk assignment response:", result);

      // Check if anything was actually created
      const createdCount = result.summary?.created || 0;
      const updatedCount = result.summary?.updated || 0;
      if (result.summary && createdCount + updatedCount === 0) {
        if (result.summary.skipped > 0) {
          throw new Error(`All ${result.summary.skipped} assignments already exist`);
        }
        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0] || "Failed to create assignments");
        }
        throw new Error("No assignments were created");
      }

      return result;
    },
    onMutate: async (assignments) => {
      // Optimistically merge assignments into caches so UI updates instantly
      const baseKey = ["/api/store-format-operator-assignments"] as const;
      const scopedKey = ["/api/store-format-operator-assignments", selectedStores.join(',')] as const;

      const prevAll = queryClient.getQueryData<any[]>(baseKey) || [];
      const prevScoped = queryClient.getQueryData<any[]>(scopedKey) || [];

      const uniqueByPair = (list: any[]) => {
        const seen = new Set<string>();
        const out: any[] = [];
        for (const a of list) {
          const key = `${a.storeId}-${a.mediaFormatId}`;
          if (!seen.has(key)) {
            seen.add(key);
            out.push(a);
          }
        }
        return out;
      };

      const optimisticItems = assignments.map(a => ({
        id: Math.floor(-Math.random() * 1_000_000),
        storeId: a.storeId,
        mediaFormatId: a.mediaFormatId,
        operatorId: a.operatorId,
        createdAt: new Date().toISOString(),
      }));

      // Update global cache
      queryClient.setQueryData<any[]>(baseKey, uniqueByPair([...prevAll, ...optimisticItems]));

      // Update scoped cache (selected stores view)
      const isInSelectedStores = (a: any) => selectedStores.includes(a.storeId);
      queryClient.setQueryData<any[]>(scopedKey, uniqueByPair([...prevScoped, ...optimisticItems.filter(isInSelectedStores)]));

      return { prevAll, prevScoped, baseKey, scopedKey };
    },
    onError: (err: any, _vars, ctx) => {
      if (ctx) {
        queryClient.setQueryData(ctx.baseKey, ctx.prevAll);
        queryClient.setQueryData(ctx.scopedKey, ctx.prevScoped);
      }
      const errorMessage = err?.message || "Failed to create bulk assignments";
      console.error("❌ Bulk assignment error:", errorMessage);
      toast({ title: "Failed to create assignments", description: errorMessage, variant: "destructive" });
    },
    onSuccess: (result) => {
      // Invalidate all store-format-operator-assignments queries
      queryClient.invalidateQueries({ queryKey: ["/api/store-format-operator-assignments"] });
      // Also invalidate store media requirements queries
      queryClient.invalidateQueries({ queryKey: ["/api/store-media-requirements"] });
      // Clear selections to avoid inadvertent reuse of volumes/operator
      setSelectedFormats([]);
      setFormatVolumes({});

      const created = result?.summary?.created || 0;
      const updatedCount = result?.summary?.updated || 0;
      const skipped = result?.summary?.skipped || 0;
      toast({
        title: "Success",
        description: `Created ${created} assignment${created !== 1 ? 's' : ''}${updatedCount > 0 ? `, updated ${updatedCount} existing` : ''}${skipped > 0 ? `, skipped ${skipped} existing` : ''}`
      });
    },
  });

  const bulkVolumeMutation = useMutation({
    mutationFn: async (volumeData: { storeId: number; mediaFormatId: number; quantity: number }[]) => {
      console.log('[BULK_VOLUME] Starting mutation with data:', volumeData);
      const response = await apiRequest("/api/store-media-requirements/bulk", "POST", { requirements: volumeData });
      const result = await response.json();
      console.log('[BULK_VOLUME] Response:', result);
      return result;
    },
    onMutate: async (volumeData) => {
      // Optimistic volume updates to requirements cache
      const baseKey = ["/api/store-media-requirements"] as const;
      const scopedKey = ["/api/store-media-requirements", retailerId] as const;

      const prevBase = queryClient.getQueryData<any[]>(baseKey) || [];
      const prevScoped = queryClient.getQueryData<any[]>(scopedKey) || [];

      const upsert = (list: any[], items: { storeId: number; mediaFormatId: number; quantity: number }[]) => {
        const map = new Map<string, any>();
        for (const r of list) map.set(`${r.storeId}-${r.mediaFormatId}`, r);
        for (const it of items) {
          const key = `${it.storeId}-${it.mediaFormatId}`;
          const existing = map.get(key) || { id: Math.floor(-Math.random() * 1_000_000), storeId: it.storeId, mediaFormatId: it.mediaFormatId, isActive: true };
          map.set(key, { ...existing, quantity: it.quantity, updatedAt: new Date().toISOString() });
        }
        return Array.from(map.values());
      };

      queryClient.setQueryData<any[]>(baseKey, upsert(prevBase, volumeData));
      queryClient.setQueryData<any[]>(scopedKey, upsert(prevScoped, volumeData));

      return { prevBase, prevScoped, baseKey, scopedKey };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        queryClient.setQueryData(ctx.baseKey, ctx.prevBase);
        queryClient.setQueryData(ctx.scopedKey, ctx.prevScoped);
      }
      toast({ title: "Failed to create bulk volumes", variant: "destructive" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/store-media-requirements", retailerId] });
      toast({ title: "Bulk volumes created successfully" });
    },
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: async ({ storeId, formatId, newOperatorId }: { storeId: number; formatId: number; newOperatorId: number }) => {
      console.log('[UPDATE_ASSIGNMENT] Starting update:', { storeId, formatId, newOperatorId });

      // Find the existing assignment
      const existingAssignment = existingAssignments.find((a: any) => a.storeId === storeId && a.mediaFormatId === formatId);
      console.log('[UPDATE_ASSIGNMENT] Found existing assignment:', existingAssignment);

      if (!existingAssignment) {
        console.error('[UPDATE_ASSIGNMENT] Assignment not found in cache');
        throw new Error("Assignment not found");
      }

      console.log('[UPDATE_ASSIGNMENT] Calling PATCH /api/store-format-operator-assignments/' + existingAssignment.id);
      const result = await apiRequest(`/api/store-format-operator-assignments/${existingAssignment.id}`, "PATCH", {
        operatorId: newOperatorId
      });
      console.log('[UPDATE_ASSIGNMENT] API call successful:', result);
      return result;
    },
    onMutate: async ({ storeId, formatId, newOperatorId }) => {
      // Optimistically swap operator in caches
      const baseKey = ["/api/store-format-operator-assignments"] as const;
      const prev = queryClient.getQueryData<any[]>(baseKey) || [];
      const next = prev.map(a => (a.storeId === storeId && a.mediaFormatId === formatId) ? { ...a, operatorId: newOperatorId, updatedAt: new Date().toISOString() } : a);
      queryClient.setQueryData<any[]>(baseKey, next);

      const scopedKey = ["/api/store-format-operator-assignments", selectedStores.join(',')] as const;
      const prevScoped = queryClient.getQueryData<any[]>(scopedKey) || [];
      const nextScoped = prevScoped.map(a => (a.storeId === storeId && a.mediaFormatId === formatId) ? { ...a, operatorId: newOperatorId, updatedAt: new Date().toISOString() } : a);
      queryClient.setQueryData<any[]>(scopedKey, nextScoped);

      return { prev, prevScoped, baseKey, scopedKey };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        queryClient.setQueryData(ctx.baseKey, ctx.prev);
        queryClient.setQueryData(ctx.scopedKey, ctx.prevScoped);
      }
      toast({ title: "Failed to update assignment", variant: "destructive" });
    },
    onSuccess: async () => {
      // Invalidate both the base query and the scoped query
      await queryClient.invalidateQueries({ queryKey: ["/api/store-format-operator-assignments"] });
      // Force refetch of the current scoped query
      await queryClient.refetchQueries({
        queryKey: ["/api/store-format-operator-assignments", selectedStores.join(',')],
        exact: true
      });
      setEditingAssignment(null);
      toast({ title: "Assignment updated successfully" });
    },
  });

  // [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_2] Enhanced delete with optimistic updates
  const deleteAssignmentMutation = useMutation({
    mutationFn: async ({ storeId, formatId }: { storeId: number; formatId: number }) => {
      const existingAssignment = existingAssignments.find((a: any) => a.storeId === storeId && a.mediaFormatId === formatId);
      if (!existingAssignment) {
        throw new Error("Assignment not found");
      }

      return apiRequest(`/api/store-format-operator-assignments/${existingAssignment.id}`, "DELETE");
    },
    onMutate: async ({ storeId, formatId }) => {
      // [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_2] Optimistically remove from cache
      const baseKey = ["/api/store-format-operator-assignments"] as const;
      const scopedKey = ["/api/store-format-operator-assignments", selectedStores.join(',')] as const;

      const prev = queryClient.getQueryData<any[]>(baseKey) || [];
      const prevScoped = queryClient.getQueryData<any[]>(scopedKey) || [];

      // Remove the assignment optimistically
      const next = prev.filter(a => !(a.storeId === storeId && a.mediaFormatId === formatId));
      const nextScoped = prevScoped.filter(a => !(a.storeId === storeId && a.mediaFormatId === formatId));

      queryClient.setQueryData<any[]>(baseKey, next);
      queryClient.setQueryData<any[]>(scopedKey, nextScoped);

      return { prev, prevScoped, baseKey, scopedKey };
    },
    onError: (_err, _vars, ctx) => {
      // [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_2] Rollback on error
      if (ctx) {
        queryClient.setQueryData(ctx.baseKey, ctx.prev);
        queryClient.setQueryData(ctx.scopedKey, ctx.prevScoped);
      }
      toast({ title: "Failed to delete assignment", variant: "destructive" });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/store-format-operator-assignments"], refetchType: 'active' });
      await queryClient.refetchQueries({ queryKey: ["/api/store-format-operator-assignments"] });
      toast({ title: "Assignment deleted successfully" });
    },
  });

  // Volume management mutations
  const updateVolumeMutation = useMutation({
    mutationFn: async (data: { storeId: number; mediaFormatId: number; quantity: number }) => {
      const existingReq = storeMediaRequirements.find(req =>
        req.storeId === data.storeId && req.mediaFormatId === data.mediaFormatId
      );

      if (existingReq) {
        return apiRequest(`/api/store-media-requirements/${existingReq.id}`, "PATCH", { quantity: data.quantity });
      } else {
        return apiRequest("/api/store-media-requirements", "POST", data);
      }
    },
    onMutate: async ({ storeId, mediaFormatId, quantity }) => {
      // Optimistically reflect volume changes
      const baseKey = ["/api/store-media-requirements", retailerId] as const;
      const prev = queryClient.getQueryData<any[]>(baseKey) || [];

      const idx = prev.findIndex(r => r.storeId === storeId && r.mediaFormatId === mediaFormatId);
      let next: any[];
      if (idx >= 0) {
        next = prev.map((r, i) => i === idx ? { ...r, quantity, updatedAt: new Date().toISOString() } : r);
      } else {
        next = [...prev, { id: Math.floor(-Math.random() * 1_000_000), storeId, mediaFormatId, quantity, isActive: true, updatedAt: new Date().toISOString() }];
      }
      queryClient.setQueryData<any[]>(baseKey, next);

      return { prev, baseKey };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx) {
        queryClient.setQueryData(ctx.baseKey, ctx.prev);
      }
      toast({ title: "Failed to update volume", variant: "destructive" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/store-media-requirements", retailerId] });
      setEditingVolume(null);
      toast({ title: "Volume updated successfully" });
    },
  });

  const handleBulkAssign = async () => {
    console.log('[BULK_ASSIGN] Starting with:', { 
      selectedOperatorId, 
      selectedStores, 
      selectedFormats,
      formatVolumes 
    });

    if (selectedStores.length === 0 || selectedFormats.length === 0) {
      toast({ title: "Please select stores and formats", variant: "destructive" });
      return;
    }

    const assignments = [];
    const volumeData = [];

    for (const storeId of selectedStores) {
      const storeSpecificOperatorId = storeOperatorOverrides[storeId] ?? null;
      const operatorIdForStore = storeSpecificOperatorId ?? selectedOperatorId;
      if (!operatorIdForStore) {
        const storeName = stores.find((store) => store.id === storeId)?.name ?? `Store ${storeId}`;
        toast({
          title: "Operator required",
          description: `Select an operator for ${storeName} (store-specific or global).`,
          variant: "destructive",
        });
        return;
      }

      for (const formatId of selectedFormats) {
        const existingAssignment = existingAssignments.find(
          (a: any) => a.storeId === storeId && a.mediaFormatId === formatId
        );
        const requirement = storeMediaRequirements.find(
          (row) => row.storeId === storeId && row.mediaFormatId === formatId,
        );

        // A retained operator row from an archived format must still be sent,
        // so the selected operator replaces the previous one before volume reactivation.
        if (shouldSendOperatorAssignment(existingAssignment, requirement)) {
          assignments.push({
            storeId,
            mediaFormatId: formatId,
            operatorId: operatorIdForStore,
          });
        }

        // Always create/update volume if a volume is set for this format
        const formatVolume = formatVolumes[formatId];
        console.log('[BULK_ASSIGN] Format volume check:', { formatId, formatVolume, condition: formatVolume !== undefined && formatVolume > 0 });

        // Only add to volumeData if user explicitly set a volume (not undefined)
        if (formatVolume !== undefined && formatVolume > 0) {
          volumeData.push({
            storeId,
            mediaFormatId: formatId,
            quantity: formatVolume,
          });
        }
      }
    }

    console.log('[BULK_ASSIGN] Final data:', { 
      assignmentsCount: assignments.length, 
      volumeDataCount: volumeData.length,
      volumeData 
    });

    // Update retained operators before volumes. Volume reactivation marks the
    // requirement active, and the server then keeps the previous operator.
    if (assignments.length > 0) {
      console.log('[BULK_ASSIGN] Calling bulkAssignMutation');
      try {
        await bulkAssignMutation.mutateAsync(assignments);
      } catch {
        return;
      }
    }

    // Create/update volumes if any are needed (even for existing assignments)
    if (volumeData.length > 0) {
      console.log('[BULK_ASSIGN] Calling bulkVolumeMutation');
      bulkVolumeMutation.mutate(volumeData);
    } else {
      console.log('[BULK_ASSIGN] NOT calling bulkVolumeMutation - volumeData is empty');
    }

    // Show appropriate message
    if (assignments.length === 0 && volumeData.length === 0) {
      toast({ title: "All selected combinations already have assignments", variant: "destructive" });
    } else if (assignments.length === 0 && volumeData.length > 0) {
      toast({ title: "Updating volumes for existing assignments" });
    }
  };

  // [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_1] Memoized filtered stores
  const filteredStores = useMemo(() => {
    return stores.filter(store =>
      store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.address.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [stores, searchTerm]);

  // [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_1] Memoized lookup maps for O(1) access
  const assignmentsByStoreFormat = useMemo(() => {
    const map = new Map<string, StoreFormatOperatorAssignment>();
    existingAssignments.forEach((a: any) => map.set(`${a.storeId}-${a.mediaFormatId}`, a));
    return map;
  }, [existingAssignments]);

  const volumesByStoreFormat = useMemo(() => {
    const map = new Map<string, number>();
    storeMediaRequirements.forEach(r => map.set(`${r.storeId}-${r.mediaFormatId}`, r.quantity));
    return map;
  }, [storeMediaRequirements]);

  const operatorsById = useMemo(() => {
    const map = new Map<number, User>();
    operators.forEach(op => map.set(op.id, op));
    return map;
  }, [operators]);

  const activeOperatorIdsByStore = useMemo(() => {
    const map = new Map<number, Set<number>>();
    existingAssignments.forEach((assignment: any) => {
      if (!map.has(assignment.storeId)) {
        map.set(assignment.storeId, new Set<number>());
      }
      map.get(assignment.storeId)?.add(assignment.operatorId);
    });
    return map;
  }, [existingAssignments]);

  const activeOperatorsByStore = useMemo(() => {
    const map = new Map<number, User[]>();
    activeOperatorIdsByStore.forEach((operatorIds, storeId) => {
      const activeOperators = Array.from(operatorIds)
        .map((operatorId) => operatorsById.get(operatorId))
        .filter((operator): operator is User => Boolean(operator))
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
      map.set(storeId, activeOperators);
    });
    return map;
  }, [activeOperatorIdsByStore, operatorsById]);

  // [BULK_ASSIGNMENTS_OPTIMIZATION_PHASE_1] Optimized helper functions using memoized maps
  const getAssignmentCount = (storeId: number, formatId: number) => {
    return assignmentsByStoreFormat.has(`${storeId}-${formatId}`) ? 1 : 0;
  };

  const getOperatorForStoreFormat = (storeId: number, formatId: number) => {
    const assignment = assignmentsByStoreFormat.get(`${storeId}-${formatId}`);
    if (assignment) {
      const operator = operatorsById.get(assignment.operatorId);
      return operator?.name || 'Unknown';
    }
    return null;
  };

  // Volume helper functions
  const getVolumeForStoreFormat = (storeId: number, formatId: number) => {
    return volumesByStoreFormat.get(`${storeId}-${formatId}`) || 0;
  };

  const handleVolumeEdit = (storeId: number, formatId: number, currentVolume: number) => {
    setEditingVolume({ storeId, formatId, currentVolume });
  };

  const handleVolumeSave = () => {
    if (!editingVolume) return;

    updateVolumeMutation.mutate({
      storeId: editingVolume.storeId,
      mediaFormatId: editingVolume.formatId,
      quantity: editingVolume.currentVolume
    });
  };

  const handleVolumeCancel = () => {
    setEditingVolume(null);
  };

  const hasOperatorSelectionCoverage = useMemo(() => {
    if (selectedStores.length === 0) return false;
    return selectedStores.every((storeId) => {
      const storeSpecificOperatorId = storeOperatorOverrides[storeId] ?? null;
      return Boolean(storeSpecificOperatorId ?? selectedOperatorId);
    });
  }, [selectedStores, storeOperatorOverrides, selectedOperatorId]);

  const storesById = useMemo(() => {
    const map = new Map<number, StoreType>();
    stores.forEach(s => map.set(s.id, s));
    return map;
  }, [stores]);

  const formatsById = useMemo(() => {
    const map = new Map<number, MediaFormat>();
    mediaFormats.forEach(f => map.set(f.id, f));
    return map;
  }, [mediaFormats]);

  const assignmentTableRows = useMemo<StoreFormatOperatorRow[]>(() => {
    return existingAssignments.map((a: any) => ({
      storeId: a.storeId,
      mediaFormatId: a.mediaFormatId,
      operatorId: a.operatorId,
      storeName: storesById.get(a.storeId)?.name || "Unknown Store",
      formatName: formatsById.get(a.mediaFormatId)?.name || "Unknown Format",
      operatorName: operatorsById.get(a.operatorId)?.name || "Unknown Operator",
      volume: getVolumeForStoreFormat(a.storeId, a.mediaFormatId),
      assignmentDate: a.createdAt || null,
      createdAt: a.createdAt,
    }));
  }, [existingAssignments, storesById, formatsById, operatorsById, volumesByStoreFormat]);

  const handleTableEditOperator = useCallback((row: StoreFormatOperatorRow) => {
    setEditingAssignment({
      storeId: row.storeId,
      formatId: row.mediaFormatId,
      currentOperatorId: row.operatorId,
    });
  }, []);

  const handleTableEditVolume = useCallback((row: StoreFormatOperatorRow) => {
    setEditingVolume({
      storeId: row.storeId,
      formatId: row.mediaFormatId,
      currentVolume: row.volume,
    });
  }, []);

  const handleTableDelete = useCallback((row: StoreFormatOperatorRow) => {
    deleteAssignmentMutation.mutate({
      storeId: row.storeId,
      formatId: row.mediaFormatId,
    });
  }, [deleteAssignmentMutation]);

  const assignmentTableColumns = useMemo(
    () =>
      createStoreFormatOperatorColumns({
        onEditOperator: handleTableEditOperator,
        onEditVolume: handleTableEditVolume,
        onDelete: handleTableDelete,
        isDeleting: deleteAssignmentMutation.isPending,
      }),
    [handleTableEditOperator, handleTableEditVolume, handleTableDelete, deleteAssignmentMutation.isPending]
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Store Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select Stores</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Input
                placeholder="Search stores..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredStores.map((store) => (
                  <div key={store.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`store-${store.id}`}
                      checked={selectedStores.includes(store.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStores([...selectedStores, store.id]);
                        } else {
                          setSelectedStores(selectedStores.filter(id => id !== store.id));
                          setStoreOperatorOverrides((prev) => {
                            const next = { ...prev };
                            delete next[store.id];
                            return next;
                          });
                        }
                      }}
                    />
                    <Label htmlFor={`store-${store.id}`} className="text-sm">
                      {store.name}
                    </Label>
                  </div>
                ))}
              </div>
              <div className="text-sm text-muted-foreground">
                Selected: {selectedStores.length} stores
              </div>

              {selectedStores.length > 0 && (
                <div className="mt-3 p-3 bg-muted rounded-lg border-2 border-blue-200">
                  <div className="text-sm font-medium text-blue-700 mb-2">
                    Selected Stores & Operators:
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedStores.map(storeId => {
                      const store = stores.find(s => s.id === storeId);
                      const activeOperators = activeOperatorsByStore.get(storeId) ?? [];
                      const selectedStoreOperatorId = storeOperatorOverrides[storeId] ?? null;
                      const hasStoreSpecificOperator = selectedStoreOperatorId !== null;
                      return (
                        <div key={storeId} className="text-sm text-blue-600 flex items-center justify-between gap-2">
                          <div className="flex-1">
                            <div className="font-medium">{store?.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {activeOperators.length > 0
                                ? `${activeOperators.length} active operator${activeOperators.length !== 1 ? "s" : ""} in this store`
                                : "No active operators found in this store"}
                            </div>
                          </div>
                          <div className="w-[240px]">
                            <Select
                              value={hasStoreSpecificOperator ? String(selectedStoreOperatorId) : "global"}
                              onValueChange={(value) => {
                                if (value === "global") {
                                  setStoreOperatorOverrides((prev) => ({ ...prev, [storeId]: null }));
                                  return;
                                }
                                setStoreOperatorOverrides((prev) => ({
                                  ...prev,
                                  [storeId]: Number(value),
                                }));
                              }}
                            >
                              <SelectTrigger className="h-8 bg-white">
                                <SelectValue placeholder="Use global operator" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="global">Use global operator</SelectItem>
                                {activeOperators.map((operator) => (
                                  <SelectItem key={operator.id} value={operator.id.toString()}>
                                    {operator.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-red-500 hover:text-red-700 hover:bg-red-50 ml-2"
                            onClick={() => {
                              setSelectedStores(selectedStores.filter(id => id !== storeId));
                              setStoreOperatorOverrides((prev) => {
                                const next = { ...prev };
                                delete next[storeId];
                                return next;
                              });
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Format Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select Media Formats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {mediaFormats.map((format) => (
                <div key={format.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`format-${format.id}`}
                      checked={selectedFormats.includes(format.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFormats([...selectedFormats, format.id]);
                          // Initialize volume to 1 when format is selected
                          setFormatVolumes({ ...formatVolumes, [format.id]: 1 });
                        } else {
                          setSelectedFormats(selectedFormats.filter(id => id !== format.id));
                          // Remove volume when unchecked
                          const newVolumes = { ...formatVolumes };
                          delete newVolumes[format.id];
                          setFormatVolumes(newVolumes);
                        }
                      }}
                    />
                    <Label htmlFor={`format-${format.id}`} className="text-sm font-medium">
                      {format.name}
                    </Label>
                  </div>
                  {selectedFormats.includes(format.id) && (
                    <div className="ml-6 flex items-center space-x-2">
                      <Label htmlFor={`volume-${format.id}`} className="text-xs text-muted-foreground">
                        Volume:
                      </Label>
                      <Input
                        id={`volume-${format.id}`}
                        type="number"
                        min="1"
                        value={formatVolumes[format.id] ?? 1}
                        onChange={(e) => {
                          const volume = parseInt(e.target.value) || 1;
                          setFormatVolumes({ ...formatVolumes, [format.id]: volume });
                        }}
                        className="w-20 h-8 text-xs"
                        placeholder="1"
                      />
                      <span className="text-xs text-muted-foreground">units</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-sm text-muted-foreground mt-2">
              Selected: {selectedFormats.length} formats
            </div>
          </CardContent>
        </Card>

        {/* Operator Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Select Operator
              <Button
                variant="outline"
                size="sm"
                onClick={refreshOperators}
                className="ml-2"
              >
                Refresh
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Select
                value={selectedOperatorId?.toString() || ""}
                onValueChange={(value) => setSelectedOperatorId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select operator" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((operator) => (
                    <SelectItem key={operator.id} value={operator.id.toString()}>
                      {operator?.name || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="text-xs text-muted-foreground">
                {operators.length} operators available for global fallback.
              </div>

              <Button
                onClick={handleBulkAssign}
                disabled={!hasOperatorSelectionCoverage || selectedStores.length === 0 || selectedFormats.length === 0 || bulkAssignMutation.isPending}
                className="w-full"
              >
                {bulkAssignMutation.isPending ? "Assigning..." : "Create Assignments"}
              </Button>

              <div className="text-sm text-muted-foreground">
                Will create {selectedStores.length * selectedFormats.length} assignments
                {selectedFormats.length > 0 && Object.keys(formatVolumes).length > 0 && (
                  <>
                    <br />
                    Volumes: {selectedFormats.map(formatId => {
                      const format = mediaFormats.find(f => f.id === formatId);
                      const volume = formatVolumes[formatId] || 0;
                      return `${format?.name}: ${volume}`;
                    }).join(', ')}
                  </>
                )}
                <br />
                (Existing assignments will be preserved)
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assignment Preview */}
      {selectedStores.length > 0 && selectedFormats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Assignment Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground mb-4">
              Preview of assignments that will be created (existing assignments shown in gray):
            </div>
            <div className="max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Store</TableHead>
                    <TableHead>Media Format</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Current Operator</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedStores.map(storeId =>
                    selectedFormats.map(formatId => {
                      const store = stores.find(s => s.id === storeId);
                      const format = mediaFormats.find(f => f.id === formatId);
                      const hasExisting = getAssignmentCount(storeId, formatId) > 0;
                      const currentOperator = getOperatorForStoreFormat(storeId, formatId);

                      return (
                        <TableRow key={`${storeId}-${formatId}`} className={hasExisting ? "text-muted-foreground" : ""}>
                          <TableCell>{store?.name}</TableCell>
                          <TableCell>{format?.name}</TableCell>
                          <TableCell>
                            <Badge variant={hasExisting ? "secondary" : "default"}>
                              {hasExisting ? "Existing" : "New"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {currentOperator ? (
                              <span className="text-sm">{currentOperator}</span>
                            ) : (
                              <span className="text-sm text-muted-foreground">Not assigned</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Assignments Table - uses shared DataTable with sorting/filtering */}
      {retailerId && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Current Assignments for {retailerName}</CardTitle>
              <div className="text-sm text-muted-foreground mt-1">
                {selectedStores.length > 0
                  ? `Showing assignments for ${selectedStores.length} selected store(s)`
                  : 'All existing operator assignments for this retailer'}
              </div>
            </div>
            {selectedStores.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedStores([]);
                  setStoreOperatorOverrides({});
                }}
              >
                Clear Selection (Show All)
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <DataTable
              columns={assignmentTableColumns}
              data={assignmentTableRows}
              searchKey="storeName"
              searchPlaceholder="Filter by store, operator, or format..."
              showColumnToggle={false}
              isLoading={assignmentsQuery.isLoading}
            />
          </CardContent>
        </Card>
      )}

      {/* Edit Operator Dialog */}
      <Dialog
        open={!!editingAssignment}
        onOpenChange={(open) => { if (!open) setEditingAssignment(null); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Assigned Operator</DialogTitle>
          </DialogHeader>
          {editingAssignment && (
            <div className="space-y-4 py-2">
              <div className="text-sm text-muted-foreground">
                Store: {storesById.get(editingAssignment.storeId)?.name}
                <br />
                Format: {formatsById.get(editingAssignment.formatId)?.name}
              </div>
              <div className="space-y-2">
                <Label>Operator</Label>
                <Select
                  value={editingAssignment.currentOperatorId.toString()}
                  onValueChange={(value) =>
                    setEditingAssignment({
                      ...editingAssignment,
                      currentOperatorId: parseInt(value),
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map((op) => (
                      <SelectItem key={op.id} value={op.id.toString()}>
                        {op.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAssignment(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!editingAssignment) return;
                updateAssignmentMutation.mutate({
                  storeId: editingAssignment.storeId,
                  formatId: editingAssignment.formatId,
                  newOperatorId: editingAssignment.currentOperatorId,
                });
              }}
              disabled={updateAssignmentMutation.isPending}
            >
              {updateAssignmentMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Volume Dialog */}
      <Dialog
        open={!!editingVolume}
        onOpenChange={(open) => { if (!open) setEditingVolume(null); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Volume</DialogTitle>
          </DialogHeader>
          {editingVolume && (
            <div className="space-y-4 py-2">
              <div className="text-sm text-muted-foreground">
                Store: {storesById.get(editingVolume.storeId)?.name}
                <br />
                Format: {formatsById.get(editingVolume.formatId)?.name}
              </div>
              <div className="space-y-2">
                <Label>Volume</Label>
                <Input
                  type="number"
                  min="0"
                  value={editingVolume.currentVolume}
                  onChange={(e) =>
                    setEditingVolume({
                      ...editingVolume,
                      currentVolume: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingVolume(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleVolumeSave}
              disabled={updateVolumeMutation.isPending}
            >
              {updateVolumeMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Skeleton component for Suspense fallback
 */
export function BulkStoreAssignmentsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <div className="h-6 bg-gray-200 rounded w-32 animate-pulse" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-10 bg-gray-200 rounded animate-pulse" />
                <div className="h-32 bg-gray-100 rounded animate-pulse" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded w-48 animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-gray-100 rounded animate-pulse" />
        </CardContent>
      </Card>
    </div>
  );
}

export default BulkStoreAssignments;
